import { useState } from "react";
import { Upload, Download, CheckCircle, AlertTriangle, FileSpreadsheet, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

/**
 * Historical CSV backfill for the Leads Performance dashboard (leads_digital).
 * - Download template (GET /api/leads-digital/csv-template)
 * - Upload historical leads (POST /api/leads-digital/upload-csv)
 *
 * Uses the same dedupe logic as the live webhook, so re-uploading is idempotent.
 */
export default function DigitalLeadsCSVCard({ onUploaded }) {
  const [csvFile, setCsvFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [jobStatus, setJobStatus] = useState(null);

  const downloadTemplate = async () => {
    setError("");
    setDownloading(true);
    try {
      const r = await api.get("/leads-digital/csv-template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "leads_digital_template.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Template descargado", { description: "leads_digital_template.csv" });
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || "Error al descargar el template";
      setError(msg);
      toast.error("Error al descargar template", { description: msg });
    } finally {
      setDownloading(false);
    }
  };

  const upload = async () => {
    if (!csvFile) {
      setError("Selecciona un archivo CSV");
      toast.warning("Selecciona un archivo CSV primero");
      return;
    }
    setError("");
    setResult(null);
    setUploading(true);
    setJobStatus({ phase: "uploading", current: 0, total: 0 });
    const fileName = csvFile.name;
    const toastId = toast.loading(`Subiendo ${fileName}…`);
    const fd = new FormData();
    fd.append("file", csvFile);
    try {
      const r = await api.post("/leads-digital/upload-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      // Async path: poll for job completion
      if (r.data?.job_id) {
        toast.loading(`Procesando ${fileName} en segundo plano…`, { id: toastId });
        const final = await pollDigitalJob(r.data.job_id);
        if (final.status === "done") {
          const d = final.result || {};
          setResult(d);
          const ins = d.inserted ?? 0;
          const upd = d.updated ?? 0;
          const skp = d.skipped ?? 0;
          const errs = (d.errors || []).length;
          toast.success(`CSV cargado · ${d.total ?? (ins + upd + skp)} filas procesadas`, {
            id: toastId,
            description: `${ins} insertados · ${upd} actualizados · ${skp} saltados${errs ? ` · ${errs} errores` : ""}`,
          });
          if (onUploaded) onUploaded(d);
        } else {
          const msg = final.error || "Error en el procesamiento";
          setError(msg);
          toast.error("Error al procesar CSV", { id: toastId, description: msg });
        }
      } else {
        setResult(r.data);
        const ins = r.data.inserted ?? 0;
        const upd = r.data.updated ?? 0;
        const skp = r.data.skipped ?? 0;
        const errs = (r.data.errors || []).length;
        toast.success(`CSV cargado · ${r.data.total ?? (ins + upd + skp)} filas procesadas`, {
          id: toastId,
          description: `${ins} insertados · ${upd} actualizados · ${skp} saltados${errs ? ` · ${errs} errores` : ""}`,
        });
        if (onUploaded) onUploaded(r.data);
      }
      setCsvFile(null);
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || "Error en la carga";
      setError(msg);
      toast.error("Error al subir CSV", { id: toastId, description: msg });
    } finally {
      setUploading(false);
      setJobStatus(null);
    }
  };

  const pollDigitalJob = async (jobId) => {
    while (true) {
      try {
        const r = await api.get(`/upload-jobs/${jobId}`);
        const d = r.data;
        setJobStatus({ phase: d.phase, current: d.progress_current || 0, total: d.progress_total || 0 });
        if (d.status === "done" || d.status === "error") return d;
      } catch (e) { /* transient */ }
      await new Promise((res) => setTimeout(res, 2000));
    }
  };

  return (
    <section className="surface p-6" data-testid="card-digital-csv" style={{ borderLeft: "3px solid #f4c400" }}>
      <div className="flex items-start gap-4 mb-5">
        <div className="kpi-icon-clean" style={{ width: 48, height: 48, color: "#f4c400", background: "#fff8d6", boxShadow: "inset 0 0 0 1px #f4c4003f" }}>
          <FileSpreadsheet size={22}/>
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="font-display text-xl font-extrabold">Backfill Histórico · CSV Leads Digitales</h2>
            <span className="pill" style={{ background: "#fef3c7", color: "#92400e", fontSize: 11, fontWeight: 800 }}>Opcional</span>
          </div>
          <p className="text-sm text-gray-500 font-semibold mt-1">
            Carga histórica de leads digitales (anteriores al webhook). Mismo motor de deduplicación que Zapier — re-subir el mismo archivo es <strong>idempotente</strong>.
          </p>
        </div>
      </div>

      <div className="mb-4 p-3 rounded-lg" style={{ background: "#fef9e7", border: "1px solid #f4c40040" }}>
        <p className="text-xs font-semibold text-gray-700">
          <strong>Paso 1:</strong> Descarga el template con los headers esperados y 3 filas de ejemplo (Meta Lead Ads, WPForms, CRM manual).
          <br/>
          <strong>Paso 2:</strong> Llena tus datos históricos respetando los headers y sube el archivo.
          <br/>
          <strong>Dedupe:</strong> <code className="bg-white px-1 rounded">meta_lead_id</code> → <code className="bg-white px-1 rounded">wp_form_id+email</code> → <code className="bg-white px-1 rounded">lead_id</code>.
        </p>
      </div>

      <div className="flex gap-3 flex-wrap mb-4">
        <button
          className="btn flex items-center gap-2"
          onClick={downloadTemplate}
          disabled={downloading}
          data-testid="digital-csv-template-btn"
        >
          {downloading ? <span className="spinner"/> : <Download size={14}/>}
          {downloading ? "Descargando…" : "Descargar Template CSV"}
        </button>
      </div>

      <div
        className={"dropzone" + (csvFile ? " drag" : "")}
        onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) setCsvFile(f); }}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => document.getElementById("digital-csv-input").click()}
        data-testid="digital-csv-dropzone"
      >
        <Upload size={32} style={{ margin: "0 auto 8px", display: "block", color: "#6b7280" }}/>
        <div className="font-bold text-base flex items-center justify-center gap-2">
          <span>{csvFile ? csvFile.name : "Arrastra tu CSV o haz clic para seleccionar"}</span>
          {csvFile && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setCsvFile(null);
                const input = document.getElementById("digital-csv-input");
                if (input) input.value = "";
              }}
              className="rounded-full p-1 hover:bg-red-100 transition"
              style={{ color: "#dc2626", lineHeight: 0 }}
              title="Quitar archivo"
              data-testid="digital-csv-remove-file"
            ><X size={14}/></button>
          )}
        </div>
        <div className="muted-small mt-2 text-xs">
          UTF-8 · separador <code>,</code> o <code>;</code> · ≤ 100 MB
        </div>
        <input id="digital-csv-input" type="file" accept=".csv" hidden onChange={(e) => setCsvFile(e.target.files?.[0])}/>
      </div>

      {/* Mode info — digital leads ingest is always append + dedup */}
      <div
        className="mt-3 p-3 rounded-xl border"
        style={{ borderColor: "#e5e7eb", background: "#fafbfc" }}
        data-testid="digital-mode-card"
      >
        <div className="font-bold mb-2" style={{ fontSize: 12, color: "#374151" }}>Modo de carga</div>
        <div className="flex items-start gap-2 text-xs">
          <input
            type="radio"
            checked
            readOnly
            style={{ marginTop: 3 }}
          />
          <div>
            <div className="font-semibold" style={{ fontSize: 12 }}>Añadir / Actualizar con dedup automático (único modo)</div>
            <div className="muted-small">
              A diferencia del CRM, los leads digitales <strong>nunca se eliminan</strong> al subir un archivo.
              El sistema deduplica por <code>meta_lead_id</code> → <code>wp_form_id+email</code> → <code>lead_id</code>:
              encontrados se <strong>actualizan</strong>, nuevos se <strong>insertan</strong>. Re-subir el mismo archivo es seguro.
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center mt-4 flex-wrap gap-3">
        <div className="text-xs text-gray-500 font-semibold">
          Headers mínimos: <code>email</code> o <code>phone</code> + al menos uno de (<code>meta_lead_id</code>, <code>wp_form_id</code>, <code>lead_id</code>) para dedupe.
        </div>
        <button
          className="btn primary flex items-center gap-2"
          onClick={upload}
          disabled={!csvFile || uploading}
          data-testid="digital-csv-upload-btn"
        >
          {uploading ? <span className="spinner"/> : <Upload size={14}/>}
          {uploading ? "Procesando…" : "Subir CSV Histórico"}
        </button>
      </div>

      {jobStatus && uploading && (
        <div className="mt-3 p-3 rounded-xl border" style={{ borderColor: "#dbeafe", background: "#eff6ff" }} data-testid="digital-upload-progress">
          <div className="flex justify-between items-center mb-2 text-xs font-bold" style={{ color: "#1e40af" }}>
            <span>{
              ({ uploading: "Subiendo archivo…", starting: "Iniciando…", upserting: "Procesando leads…",
                 archiving: "Archivando snapshot…", queued: "En cola…", done: "Listo" }[jobStatus.phase] || "Procesando…")
            }</span>
            <span className="font-mono">
              {jobStatus.total > 0 ? `${jobStatus.current.toLocaleString()} / ${jobStatus.total.toLocaleString()}` : "…"}
            </span>
          </div>
          <div style={{ height: 6, background: "#dbeafe", borderRadius: 999, overflow: "hidden" }}>
            <div style={{
              height: "100%",
              width: jobStatus.total > 0 ? `${Math.min(100, (jobStatus.current / jobStatus.total) * 100)}%` : "10%",
              background: "linear-gradient(90deg, #3b82f6, #2563eb)",
              transition: "width 0.4s ease",
            }}/>
          </div>
        </div>
      )}
      {error && (
        <div className="mt-3 p-3 rounded-xl border border-red-200 bg-red-50 text-red-800 font-semibold text-sm flex items-start gap-2" data-testid="digital-csv-error">
          <AlertTriangle size={16} className="flex-shrink-0 mt-0.5"/>
          <div>{error}</div>
        </div>
      )}
      {result && (
        <div className="mt-3 p-3 rounded-xl border border-green-200 bg-green-50 text-green-900 font-semibold text-sm flex items-start gap-2" data-testid="digital-csv-success">
          <CheckCircle size={16} className="flex-shrink-0 mt-0.5"/>
          <div>
            ¡Carga exitosa en <strong>{result.elapsed_ms}ms</strong>!
            <div className="text-xs mt-1 font-semibold opacity-80">
              Insertados: <strong>{result.inserted ?? 0}</strong> · Actualizados: <strong>{result.updated ?? 0}</strong> · Saltados: <strong>{result.skipped ?? 0}</strong>
              {result.errors && result.errors.length > 0 && <> · Errores: <strong>{result.errors.length}</strong></>}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
